import React, { useMemo } from "react"

import { Flex, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import TableComponent from "../../../../Components/Table"
import Icon from "../../../../Icon"
import { useCountryOlympicsLocked } from "../../hooks/useCountryOlympicsLocked"
import { olympicComingSoonData, olympicEmptyData } from "../../utils/emptyData"
import ShopifyComingSoonBanner from "../ShopifyComingSoonBanner"
import { formatCount, getColumns } from "../Table/chargebackOlympicsColumns"

import "./styles.less"

const { Title, Text, Link } = Typography

const ChargebackOlympics = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const fetching = useSelector((state) => state.health.dataOlympicsFetching)
  const dataOlympics = useSelector((state) => state.health.dataOlympics)
  const { is_business_health_available } = useSelector((state) => state.health.mainInfo)

  const isLocked = useCountryOlympicsLocked()
  const { count, results } = isLocked
    ? olympicComingSoonData
    : is_business_health_available
      ? dataOlympics
      : olympicEmptyData

  const tableData = useMemo(() => (Array.isArray(results) ? results.slice(0, 3) : []), [results])

  const columnsByCardType = getColumns({ t })

  const navigateToFullTable = () => {
    navigate("chargeback_olympics")
  }

  const renderCustomerName = (record) => {
    if (
      !record?.customer_name ||
      record?.customer_name === "N/A" ||
      record?.customer_name === "-"
    ) {
      return <Text>{record?.customer_name || "-"}</Text>
    }

    const customerLink = record?.stripe_link || record?.shopify_link
    if (customerLink) {
      return (
        <Link href={customerLink} target="_blank">
          <Private>{record.customer_name}</Private>
        </Link>
      )
    }

    return (
      <Link>
        <Private>{record.customer_name}</Private>
      </Link>
    )
  }

  const renderCount = (value) => (value === "-" ? "-" : formatCount(value))

  return (
    <Flex gap={24} vertical={true} className="card-block-with-table">
      <Spin spinning={fetching}>
        <Flex
          align="flex-start"
          justify="space-between"
          style={{ marginBottom: 16, padding: "0 16px" }}
        >
          <Title level={5} style={{ marginBottom: 0, fontWeight: 600 }}>
            {t("Chargeback Olympics")}
          </Title>

          {is_business_health_available && !isMobile && !isLocked && (
            <ButtonComponent onClick={navigateToFullTable}>{t("View All")}</ButtonComponent>
          )}
        </Flex>

        {isMobile ? (
          <Flex vertical={true} className="chargeback-olympics-mobile-list">
            {tableData.map((record, index) => (
              <div
                key={record.sequence_number}
                className={`chargeback-olympics-mobile-list__item${index === tableData.length - 1 ? " chargeback-olympics-mobile-list__item--last" : ""}`}
              >
                <Flex vertical={true} gap={8}>
                  <Text type="secondary" className="chargeback-olympics-mobile-list__label">
                    {t("Customer name")}
                  </Text>

                  <Flex gap={8} align="center">
                    {record?.sequence_number <= 3 ? (
                      <Icon type={`medal${record.sequence_number}`} />
                    ) : (
                      <Text className="chargeback-olympics-mobile-list__rank">
                        {record.sequence_number}
                      </Text>
                    )}
                    {renderCustomerName(record)}
                  </Flex>

                  <Text type="secondary" className="chargeback-olympics-mobile-list__label">
                    {t("Email")}
                  </Text>
                  <Text strong>{record?.email ? <Private>{record.email}</Private> : "-"}</Text>

                  <Flex gap={12} className="chargeback-olympics-mobile-list__metrics">
                    <Flex
                      vertical={true}
                      gap={2}
                      className="chargeback-olympics-mobile-list__metric-block"
                    >
                      <Text type="secondary" className="chargeback-olympics-mobile-list__label">
                        {t("Alerts")}
                      </Text>
                      <Text strong>{renderCount(record?.alerts_count)}</Text>
                    </Flex>

                    <Flex
                      vertical={true}
                      gap={2}
                      className="chargeback-olympics-mobile-list__metric-block"
                    >
                      <Text type="secondary" className="chargeback-olympics-mobile-list__label">
                        {t("Chargebacks")}
                      </Text>
                      <Text strong>{renderCount(record?.chargebacks_count)}</Text>
                    </Flex>
                  </Flex>
                </Flex>
              </div>
            ))}

            {is_business_health_available && !isLocked && (
              <ButtonComponent
                className="chargeback-olympics-mobile-list__view-all"
                onClick={navigateToFullTable}
              >
                {t("View all")}
              </ButtonComponent>
            )}
          </Flex>
        ) : (
          <TableComponent
            columns={columnsByCardType}
            rowKey={"sequence_number"}
            loading={fetching}
            dataSource={results}
            scroll={{ x: "auto", y: 248 }}
            showSizeChanger={false}
            withOutPagination={true}
            className="chargeback-olympics-table"
          />
        )}
      </Spin>

      {isLocked && <ShopifyComingSoonBanner />}
    </Flex>
  )
}

export default React.memo(ChargebackOlympics)
